import { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface SubscriptionStatus {
  subscribed: boolean;
  onTrial: boolean;
  product_id: string | null;
  subscription_end: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signOut: () => Promise<void>;
  isLoading: boolean;
  subscription: SubscriptionStatus | null;
  checkSubscription: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  testMode: boolean;
  toggleTestMode: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signOut: async () => {},
  isLoading: true,
  subscription: null,
  checkSubscription: async () => {},
  isAdmin: false,
  isSuperAdmin: false,
  testMode: false,
  toggleTestMode: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [testMode, setTestMode] = useState(() => {
    if (import.meta.env.DEV) {
      return localStorage.getItem('testMode') === 'true';
    }
    return false;
  });
  const navigate = useNavigate();
  
  // Ref-based lock to prevent duplicate subscription checks (avoids stale closure issues)
  const isCheckingRef = useRef(false);
  const subscriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkSubscription = async (tokenOverride?: string) => {
    // Use ref for reliable duplicate prevention (state can be stale in closures)
    if (isCheckingRef.current) {
      return;
    }

    // Get fresh session to avoid stale token issues
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const accessToken = tokenOverride || currentSession?.access_token;
    
    if (!accessToken) {
      setSubscription(null);
      setIsCheckingSubscription(false);
      isCheckingRef.current = false;
      return;
    }

    // Lock and set state
    isCheckingRef.current = true;
    setIsCheckingSubscription(true);

    // Clear any existing timeout
    if (subscriptionTimeoutRef.current) {
      clearTimeout(subscriptionTimeoutRef.current);
      subscriptionTimeoutRef.current = null;
    }

    // Test mode bypasses Stripe checks (dev only)
    if (testMode && import.meta.env.DEV) {
      setSubscription({
        subscribed: true,
        onTrial: false,
        product_id: 'test_mode',
        subscription_end: null
      });
      setIsCheckingSubscription(false);
      isCheckingRef.current = false;
      return;
    }

    // Safety timeout - 5 seconds max to prevent indefinite loading
    // If it fails, we default to "subscribed" (optimistic) to prevent blocking the user
    subscriptionTimeoutRef.current = setTimeout(() => {
      console.warn('Subscription check timed out - allowing access optimistically');
      setSubscription({ 
        subscribed: true,
        onTrial: false, 
        product_id: null, 
        subscription_end: null 
      });
      setIsCheckingSubscription(false);
      isCheckingRef.current = false;
    }, 5000);

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Clear timeout on success
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
        subscriptionTimeoutRef.current = null;
      }

      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      // Clear timeout on error
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
        subscriptionTimeoutRef.current = null;
      }
      console.error('Subscription check failed', error);
      // Fallback: allow access temporarily to avoid locking users out
      setSubscription({ 
        subscribed: true,
        onTrial: false, 
        product_id: null, 
        subscription_end: null 
      });
    } finally {
      // ALWAYS release the lock
      setIsCheckingSubscription(false);
      isCheckingRef.current = false;
    }
  };

  const checkAdminRole = async (sessionOverride?: Session | null) => {
    const activeSession = sessionOverride ?? session;
    
    if (!activeSession) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    try {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', activeSession.user.id)
        .in('role', ['admin', 'super_admin']);

      if (error) {
        console.error('Role check failed');
        setIsAdmin(false);
        setIsSuperAdmin(false);
        return;
      }

      const roleList = roles?.map(r => r.role) || [];
      const hasSuperAdmin = roleList.includes('super_admin');
      const hasAdmin = roleList.includes('admin');

      setIsSuperAdmin(hasSuperAdmin);
      setIsAdmin(hasSuperAdmin || hasAdmin);
    } catch (error) {
      console.error('Role check failed');
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  };

  const toggleTestMode = () => {
    if (import.meta.env.DEV) {
      const newTestMode = !testMode;
      setTestMode(newTestMode);
      localStorage.setItem('testMode', String(newTestMode));
      setTimeout(() => {
        if (session) {
          checkSubscription();
        }
      }, 100);
    }
  };

  useEffect(() => {
    const SESSION_INIT_TIMEOUT = 10000;
    let sessionInitTimeoutId: NodeJS.Timeout | null = null;
    let isInitComplete = false;
    let authEventCount = 0;

    if (import.meta.env.DEV) {
      console.log('[AuthContext] Session initialization started');
    }

    // Set up auth state listener FIRST
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        authEventCount++;
        if (import.meta.env.DEV) {
          console.log(`[AuthContext] Auth event #${authEventCount}: ${event}`, {
            hasSession: !!newSession,
            userId: newSession?.user?.id?.substring(0, 8) + '...',
            expiresAt: newSession?.expires_at ? new Date(newSession.expires_at * 1000).toISOString() : null,
          });
        }

        // Mark init as complete if we get an auth event
        isInitComplete = true;
        if (sessionInitTimeoutId) {
          clearTimeout(sessionInitTimeoutId);
          sessionInitTimeoutId = null;
        }

        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);
        
        if (event === "SIGNED_IN" && newSession) {
          if (import.meta.env.DEV) {
            console.log('[AuthContext] User signed in, checking subscription and admin role');
          }
          checkSubscription(newSession.access_token);
          checkAdminRole(newSession);
          navigate("/home");
        } else if (event === "SIGNED_OUT") {
          if (import.meta.env.DEV) {
            console.log('[AuthContext] User signed out, clearing state');
          }
          setSubscription(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
        } else if (event === "TOKEN_REFRESHED" && newSession) {
          if (import.meta.env.DEV) {
            console.log('[AuthContext] Token refreshed');
          }
          checkSubscription(newSession.access_token);
        }
      }
    );

    const initSession = async () => {
      try {
        if (import.meta.env.DEV) {
          console.log('[AuthContext] initSession called');
        }
        const { data: { session: existingSession }, error } = await supabase.auth.getSession();
        
        if (import.meta.env.DEV) {
          console.log('[AuthContext] getSession result:', {
            hasSession: !!existingSession,
            hasError: !!error,
            errorCode: error?.code,
            errorMessage: error?.message,
          });
        }

        if (error) {
          if (import.meta.env.DEV) {
            console.error('[AuthContext] Session initialization failed:', error);
          }
          await supabase.auth.signOut();
          setIsLoading(false);
          return;
        }
        
        if (!existingSession) {
          if (import.meta.env.DEV) {
            console.log('[AuthContext] No session found, user needs to log in');
          }
          isInitComplete = true;
          setIsLoading(false);
          return;
        }
        
        if (existingSession?.expires_at) {
          const expiresAt = new Date(existingSession.expires_at * 1000);
          const now = new Date();
          const bufferMinutes = 5;
          const timeUntilExpiry = expiresAt.getTime() - now.getTime();
          
          if (import.meta.env.DEV) {
            console.log('[AuthContext] Session expires at:', expiresAt.toISOString(), 
              'Time until expiry (ms):', timeUntilExpiry);
          }
          
          if (timeUntilExpiry < bufferMinutes * 60 * 1000) {
            if (import.meta.env.DEV) {
              console.log('[AuthContext] Session is about to expire, refreshing...');
            }
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              if (import.meta.env.DEV) {
                console.error('[AuthContext] Session refresh failed:', refreshError);
              }
              await supabase.auth.signOut();
              setSession(null);
              setUser(null);
              setIsLoading(false);
              return;
            }
            if (refreshData.session) {
              if (import.meta.env.DEV) {
                console.log('[AuthContext] Session refreshed successfully');
              }
              setSession(refreshData.session);
              setUser(refreshData.session.user);
              checkSubscription(refreshData.session.access_token);
              checkAdminRole(refreshData.session);
            }
          } else {
            if (import.meta.env.DEV) {
              console.log('[AuthContext] Session is valid, using existing session');
            }
            setSession(existingSession);
            setUser(existingSession.user);
            checkSubscription(existingSession.access_token);
            checkAdminRole(existingSession);
          }
        } else if (existingSession) {
          if (import.meta.env.DEV) {
            console.log('[AuthContext] Session has no expiry, using existing session');
          }
          setSession(existingSession);
          setUser(existingSession.user);
          checkSubscription(existingSession.access_token);
          checkAdminRole(existingSession);
        }
        
        isInitComplete = true;
        setIsLoading(false);
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Session initialization complete');
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('[AuthContext] Session initialization failed:', err);
        }
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setIsLoading(false);
      }
    };

    sessionInitTimeoutId = setTimeout(async () => {
      if (!isInitComplete) {
        if (import.meta.env.DEV) {
          console.warn('[AuthContext] Session initialization timed out after 10 seconds');
          console.warn('[AuthContext] Auth events received:', authEventCount);
        }
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setSubscription(null);
        setIsLoading(false);
      }
    }, SESSION_INIT_TIMEOUT);

    initSession();

    return () => {
      authSubscription.unsubscribe();
      // Force-reset subscription loading state on unmount
      setIsCheckingSubscription(false);
      isCheckingRef.current = false;
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
      }
      if (sessionInitTimeoutId) {
        clearTimeout(sessionInitTimeoutId);
      }
    };
  }, [navigate]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      signOut, 
      isLoading: isLoading || isCheckingSubscription, 
      subscription, 
      checkSubscription, 
      isAdmin, 
      isSuperAdmin,
      testMode, 
      toggleTestMode 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
